import { useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Button, CircularProgress, Stack } from '@mui/material';
import { SaveIcon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import type { Property_Searches_Bool_Exp } from '@graphql/__generated__/graphql';
import { PropertySearchesList } from '@modules/_shared/components/property-search/PropertySearchesList';
import { usePropertySearchQuickFilters } from '@modules/_shared/components/property-search/usePropertySearchQuickFilters';
import { isDeepEqual } from '@modules/_shared/utils/objects';
import { useLocale } from '@shared/hooks/locale';

type PropertySearchesMatchingProps = {
  defaultFilters?: Property_Searches_Bool_Exp;
  initialCriteria?: Property_Searches_Bool_Exp | null;
  onAdd: (ids: string[]) => void;
  isAdding?: boolean;
  onCancel: () => void;
  onSaveCriteria?: (criteria: Property_Searches_Bool_Exp | null) => void;
  isSavingCriteria?: boolean;
};

export const PropertySearchesMatching: React.FC<
  PropertySearchesMatchingProps
> = ({
  defaultFilters,
  onAdd,
  onCancel,
  isAdding,
  onSaveCriteria,
  isSavingCriteria,
  initialCriteria,
}) => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  // We use a set to prevent adding duplicates when selecting users.
  const [selectedUserIds, setSelectedUserIds] = useState(new Set<string>());
  const quickFilters = usePropertySearchQuickFilters({
    disableOfferType: true,
  });

  const criteriasHaveChanged = useMemo(() => {
    return !isDeepEqual(
      initialCriteria,
      JSON.parse(searchParams.get('matching_where') ?? '{}'),
    );
  }, [initialCriteria, searchParams]);

  return (
    <PropertySearchesList
      hideTitle
      defaultFilters={defaultFilters}
      queryParamsScope="matching"
      forceEditMode
      quickFilters={quickFilters}
      onSelectionChange={searches =>
        setSelectedUserIds(
          new Set(searches.map(({ user }) => user?.id).filter(v => v != null)),
        )
      }
      toolbarActions={
        <Stack
          spacing={1}
          direction="row"
          sx={{
            p: 1,
          }}
        >
          {onSaveCriteria != null && (
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isSavingCriteria}
              disabled={!criteriasHaveChanged}
              loadingIndicator={
                <CircularProgress disableShrink color="inherit" size={16} />
              }
              loadingPosition="start"
              startIcon={<SaveIcon />}
              onClick={() =>
                onSaveCriteria(
                  JSON.parse(searchParams.get('matching_where') ?? '{}'),
                )
              }
            >
              {t('Save criteria')}
            </LoadingButton>
          )}
          <LoadingButton
            variant="contained"
            color="primary"
            loading={isAdding}
            disabled={selectedUserIds.size === 0}
            loadingIndicator={
              <CircularProgress disableShrink color="inherit" size={16} />
            }
            onClick={() => onAdd(Array.from(selectedUserIds))}
          >
            {t('Add buyers')}
          </LoadingButton>
          <Button variant="outlined" onClick={onCancel}>
            {t('Cancel')}
          </Button>
        </Stack>
      }
    />
  );
};
